import type { ResolvedProofreadingBatchChecks } from "@/lib/api/proofreadingAi";

/** FE で選択・表示する AI 校正チェック種別（固有名詞チェックは除外）。並びは選択画面の表示順。 */
export const AI_PROOFREADING_CHECK_IDS = [
  "errorCheck",
  "expressionCheck",
  "storyCheck",
  "foreshadowCheck",
] as const;

export type AiProofreadingCheckId = (typeof AI_PROOFREADING_CHECK_IDS)[number];

/** 画面には出すが、リリース前のため選択できない項目。 */
export const AI_PROOFREADING_UNRELEASED_CHECK_IDS: readonly AiProofreadingCheckId[] = [
  "foreshadowCheck",
];

export function isAiProofreadingCheckSelectable(checkId: AiProofreadingCheckId): boolean {
  return !(AI_PROOFREADING_UNRELEASED_CHECK_IDS as readonly string[]).includes(checkId);
}

/** BE 結果 JSON の全チェック種別（過去バッチの noun_check 読み取り用）。 */
export const AI_PROOFREADING_ALL_CHECK_IDS = [
  ...AI_PROOFREADING_CHECK_IDS,
  "nounCheck",
] as const;

export type AiProofreadingAllCheckId = (typeof AI_PROOFREADING_ALL_CHECK_IDS)[number];

/** BE 結果 JSON のキー（snake_case）。 */
export const AI_PROOFREADING_RESULT_KEYS: Record<AiProofreadingAllCheckId, string> = {
  errorCheck: "error_check",
  nounCheck: "noun_check",
  storyCheck: "story_check",
  expressionCheck: "expression_check",
  foreshadowCheck: "foreshadow_check",
};

/**
 * 旧結果（`type` なし）向けの i18n フォールバック。
 * 新規 code は BE の `aiProofreadingIssueCatalog` に足し、結果の `type` を表示する。
 */
export const AI_PROOFREADING_ERROR_CODE_I18N: Record<string, string> = {
  missing_particles: "proofreadingReviseManuscriptPage.aiIssueTypes.missing_particles",
  missing_converting: "proofreadingReviseManuscriptPage.aiIssueTypes.missing_converting",
  idiom_misuse: "proofreadingReviseManuscriptPage.aiIssueTypes.idiom_misuse",
  word_sense_error: "proofreadingReviseManuscriptPage.aiIssueTypes.word_sense_error",
  ruby_reading_error: "proofreadingReviseManuscriptPage.aiIssueTypes.ruby_reading_error",
  missing_typing: "proofreadingReviseManuscriptPage.aiIssueTypes.missing_typing",
  syntax_collapse: "proofreadingReviseManuscriptPage.aiIssueTypes.syntax_collapse",
  syntax_refine: "proofreadingReviseManuscriptPage.aiIssueTypes.syntax_refine",
  syntax_refine_delete: "proofreadingReviseManuscriptPage.aiIssueTypes.syntax_refine_delete",
  syntax_refine_punctuation:
    "proofreadingReviseManuscriptPage.aiIssueTypes.syntax_refine_punctuation",
  syntax_refine_reorder: "proofreadingReviseManuscriptPage.aiIssueTypes.syntax_refine_reorder",
  syntax_refine_tense: "proofreadingReviseManuscriptPage.aiIssueTypes.syntax_refine_tense",
  naming_inconsistency: "proofreadingReviseManuscriptPage.aiIssueTypes.naming_inconsistency",
  new_proper_nouns: "proofreadingReviseManuscriptPage.aiIssueTypes.new_proper_nouns",
  worldbuilding_mismatch: "proofreadingReviseManuscriptPage.aiIssueTypes.worldbuilding_mismatch",
  storyline_inconsistency: "proofreadingReviseManuscriptPage.aiIssueTypes.storyline_inconsistency",
  character_behavior_mismatch: "proofreadingReviseManuscriptPage.aiIssueTypes.character_behavior_mismatch",
  twist_plausibility_issue: "proofreadingReviseManuscriptPage.aiIssueTypes.twist_plausibility_issue",
  mood_context_disruption: "proofreadingReviseManuscriptPage.aiIssueTypes.mood_context_disruption",
  cliche_expression: "proofreadingReviseManuscriptPage.aiIssueTypes.cliche_expression",
  redundant_connective: "proofreadingReviseManuscriptPage.aiIssueTypes.redundant_connective",
  verbose_expression: "proofreadingReviseManuscriptPage.aiIssueTypes.verbose_expression",
  repetitive_narration: "proofreadingReviseManuscriptPage.aiIssueTypes.repetitive_narration",
  repetitive_dialogue: "proofreadingReviseManuscriptPage.aiIssueTypes.repetitive_dialogue",
  recollection_mismatch: "proofreadingReviseManuscriptPage.aiIssueTypes.recollection_mismatch",
  skill_name_call: "proofreadingReviseManuscriptPage.aiIssueTypes.skill_name_call",
  manga_gesture: "proofreadingReviseManuscriptPage.aiIssueTypes.manga_gesture",
  naked_sfx: "proofreadingReviseManuscriptPage.aiIssueTypes.naked_sfx",
  anime_spectacle: "proofreadingReviseManuscriptPage.aiIssueTypes.anime_spectacle",
  genre_vocabulary_shift:
    "proofreadingReviseManuscriptPage.aiIssueTypes.genre_vocabulary_shift",
  blank_line_timeskip:
    "proofreadingReviseManuscriptPage.aiIssueTypes.blank_line_timeskip",
  pov_switching: "proofreadingReviseManuscriptPage.aiIssueTypes.pov_switching",
  ellipsis_dash_overuse:
    "proofreadingReviseManuscriptPage.aiIssueTypes.ellipsis_dash_overuse",
  punctuation_density:
    "proofreadingReviseManuscriptPage.aiIssueTypes.punctuation_density",
  lexical_echo: "proofreadingReviseManuscriptPage.aiIssueTypes.lexical_echo",
  quote_mark_usage:
    "proofreadingReviseManuscriptPage.aiIssueTypes.quote_mark_usage",
  insufficient_foreshadowing:
    "proofreadingReviseManuscriptPage.aiIssueTypes.insufficient_foreshadowing",
  late_or_thin_foreshadowing:
    "proofreadingReviseManuscriptPage.aiIssueTypes.late_or_thin_foreshadowing",
  revealed_too_early: "proofreadingReviseManuscriptPage.aiIssueTypes.revealed_too_early",
  adequate_but_strengthen:
    "proofreadingReviseManuscriptPage.aiIssueTypes.adequate_but_strengthen",
  dangling_promise: "proofreadingReviseManuscriptPage.aiIssueTypes.dangling_promise",
  pacing_flashback_ratio:
    "proofreadingReviseManuscriptPage.aiIssueTypes.pacing_flashback_ratio",
  climax_stacking: "proofreadingReviseManuscriptPage.aiIssueTypes.climax_stacking",
  offscreen_gap: "proofreadingReviseManuscriptPage.aiIssueTypes.offscreen_gap",
  exposition_in_climax:
    "proofreadingReviseManuscriptPage.aiIssueTypes.exposition_in_climax",
  scene_template_repeat:
    "proofreadingReviseManuscriptPage.aiIssueTypes.scene_template_repeat",
};

/** i18n ラベル・説明・警告キー。 */
export const AI_PROOFREADING_CHECK_I18N: Record<
  AiProofreadingCheckId,
  { labelKey: string; descriptionKey: string; warningKey?: string }
> & {
  nounCheck?: { labelKey: string; descriptionKey: string; warningKey?: string };
} = {
  errorCheck: {
    labelKey: "proofreadingSelectChecksPage.checks.errorCheck.label",
    descriptionKey: "proofreadingSelectChecksPage.checks.errorCheck.description",
  },
  nounCheck: {
    labelKey: "proofreadingSelectChecksPage.checks.nounCheck.label",
    descriptionKey: "proofreadingSelectChecksPage.checks.nounCheck.description",
    warningKey: "proofreadingSelectChecksPage.checks.nounCheck.warning",
  },
  storyCheck: {
    labelKey: "proofreadingSelectChecksPage.checks.storyCheck.label",
    descriptionKey: "proofreadingSelectChecksPage.checks.storyCheck.description",
    warningKey: "proofreadingSelectChecksPage.checks.storyCheck.warning",
  },
  expressionCheck: {
    labelKey: "proofreadingSelectChecksPage.checks.expressionCheck.label",
    descriptionKey: "proofreadingSelectChecksPage.checks.expressionCheck.description",
  },
  foreshadowCheck: {
    labelKey: "proofreadingSelectChecksPage.checks.foreshadowCheck.label",
    descriptionKey: "proofreadingSelectChecksPage.checks.foreshadowCheck.description",
    warningKey: "proofreadingSelectChecksPage.checks.foreshadowCheck.warning",
  },
};

/** AI校正の単価。選択項目は1種だけなので、文字数にこの倍率を掛ける。 */
export const AI_PROOFREADING_FISH_PER_CHAR = 5;

/** β版の1回あたり選択上限。 */
export const AI_PROOFREADING_MAX_CHARS = 30_000;

export const AI_PROOFREADING_MAX_ISSUES_PER_CHECK = 150;

export function isAiProofreadingCheckEnabled(
  checks: ResolvedProofreadingBatchChecks,
  checkId: AiProofreadingCheckId,
): boolean {
  return checks[checkId] === true;
}
